import axios from 'axios';
import { ClientInterface, ConfigurationInterface, SubsidiaryInterface } from '@e3labs/isa-data-dictionary';
import { call, all, put, takeLatest, select, delay } from 'redux-saga/effects';
import currency from 'currency.js';
import { saveAs } from 'file-saver';
import { ISA_ERROR, ORDER_STATUS_ALL, PAYMENT_OPTION_TYPE, USER_ROLE } from '~/constants';
import {
  GET_ALL_FILTER_SELECTS_REQUEST,
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SHARE_PDF,
  DELETE_OR_CANCEL_ITEM,
  ORDER_STATUS,
  RESEND_ITEM,
  CANCEL_ORDER_TOTVS,
  DOWNLOAD_EXCEL
} from './order-constants';

import domainService from '~/services/modules/domain/domain-service';

import * as orderActions from './order-actions';

import { toDecimals } from '~/utils/format';
import { formatDateUTCMinus3ToFilter, formatMessage } from '~/i18n/translate';

import { AppStateType } from '../root-reducer';
import history from '~/services/history';
import orderService from './order-service';
import notification from '~/services/notification';
import clientService from '../client/client-service';
import reasonService from '../reason/reason-service';
import userService from '~/services/modules/user/user-service';
import { validateBudgetCopy } from './order-create-selectors';
import { selectorConfiguration } from '../configuration/configuration-selectors';
import { PixPaymentProps } from './order-interface';
import { sort } from './order-utils';

let cancel;
const CancelToken = axios.CancelToken;

export function* getAll({ payload }: any) {
  try {
    if (cancel !== undefined) {
      cancel();
    }
    yield put(orderActions.setLoadingData(true));

    const { page, rowsPerPage, order } = yield select(state => state.order.list);
    const { user } = yield select((state: AppStateType) => state.auth);

    let sellers;

    if (
      user?.userRole === USER_ROLE.DIRECTOR ||
      user?.userRole === USER_ROLE.MANAGER ||
      user?.userRole === USER_ROLE.SUPERVISOR
    ) {
      const { data: _sellers } = yield call(orderService.getSellers);

      const sellersLevel = [...userService.buildSellersLevel([..._sellers], user)];

      const initialSellers = userService.getInitialSellers(sellersLevel, user);

      sellers = [...initialSellers];
    } else {
      sellers = [user?.externalCode];
    }

    const startDateRaw = payload?.dateRange?.[0] ? new Date(payload.dateRange[0]) : new Date();
    const endDateRaw = payload?.dateRange?.[1] ? new Date(payload.dateRange[1]) : new Date();

    const startDate = formatDateUTCMinus3ToFilter(startDateRaw, false);
    const endDate = formatDateUTCMinus3ToFilter(endDateRaw, true);

    const params = {
      ids: payload?.sellersSelecteds?.length === 0 ? sellers : payload?.sellersSelecteds,
      status:
        payload?.status?.length > 0
          ? payload?.status?.find(status => (status?.key || status) === 'all')
            ? ORDER_STATUS_ALL.filter(status => status !== ORDER_STATUS.CANCELED_BUDGET)
            : payload?.status?.map(status => status?.key || status)
          : ORDER_STATUS_ALL.filter(status => status !== ORDER_STATUS.CANCELED_BUDGET),
      startDate,
      endDate,
      orderAsc: order === 'asc' ?? 'asc',
      orderNumber: payload?.orderNumber ?? undefined,
      externalOrder: payload?.externalOrder ?? undefined,
      orderType: payload?.orderType?.map(type => type.key) ?? undefined,
      clientExternalCode: payload?.client?.externalCode ?? undefined,
      invoiceNumber: payload?.invoiceNumber ?? undefined,
      orderExternalNumber: payload?.orderExternalNumber ?? undefined,
      subsidiaryExternalCodes: payload?.filiais ?? undefined,
      getNames: true
    };

    const {
      data: { count: rowsCount }
    } = yield call(orderService.getCountOrders, params);

    const { data } = yield call(
      orderService.getOrders,
      { ...params, skip: page === 0 ? 0 : page * rowsPerPage, limit: rowsPerPage },
      {
        cancelToken: new CancelToken(function executor(c) {
          cancel = c;
        })
      }
    );

    if (data) {
      yield put(
        orderActions.getAllSuccess({
          rows: [
            ...data.map((order: any) => ({
              ...order,
              orderTotal: order.orderItems?.reduce(
                (acumulator: number, item: any) => acumulator + item?.soldPrice * item?.soldQuantity,
                0
              )
            }))
          ],
          rowsCount,
          total: undefined
        })
      );
    }

    yield put(orderActions.setLoadingData(false));

    let totalOrdersParsed;

    const { data: totalOrders } = yield call(orderService.getTotalOrders, params);

    if (params?.status?.length === 0) {
      totalOrdersParsed = {
        ...totalOrders,
        budget: 0,
        total: totalOrders?.total - totalOrders?.budget
      };
    } else {
      totalOrdersParsed = totalOrders;
    }

    if (data) {
      yield put(
        orderActions.getAllSuccess({
          rows: [
            ...data.map((order: any) => ({
              ...order,
              orderTotal: order.orderItems?.reduce(
                (acumulator: number, item: any) => acumulator + item?.soldPrice * item?.soldQuantity,
                0
              )
            }))
          ],
          rowsCount,
          total: totalOrdersParsed
        })
      );
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById({ id }: any) {
  try {
    yield put(orderActions.setLoadingData(true));
    yield put(orderActions.setLoadingVinculatedOrder(true));

    const configuration: ConfigurationInterface = yield select(selectorConfiguration);
    const { data: order } = yield call(orderService.getOrder, id);
    const user = yield select((state: AppStateType) => state.auth.user);

    let [{ data: subsidiaries }, { data: clientAddress }, { data: reasons }] = yield all([
      call(domainService.getSubsidiaries, { userSubsidiaryFilter: user?.userSubsidiaries ?? [] }),
      call(domainService.getClientAddress, {
        clientExternalCode: order?.client?.externalCode,
        addressCode: order?.orderDetails?.deliveryAddressCode
      }),
      call(reasonService.getAll)
    ]);

    const subsidiary: SubsidiaryInterface = subsidiaries.find(
      item => item.externalCode === order.orderDetails?.subsidiaryExternalCode
    );

    const blockSubsidiary: boolean = reasons?.some(x => x?.subsidiaryExternalCode);

    let blockingSubsidiaryReason = undefined;

    if (blockSubsidiary) {
      const reason = reasons?.find(x => x.subsidiaryExternalCode === subsidiary?.externalCode);

      if (reason) {
        blockingSubsidiaryReason = reason;
      }
    }

    if (order) {
      //! Caso precisar usar o availablePromotions, decomentar abaixo

      // { data: clientPrices } =>  call(domainService.getClientPrices),
      // const priceTableCode = getPriceTable(clientPrices, subsidiary, order.client);

      // const { data: availablePromotions } = yield call(promotionService.getSpecificsPromotions, {
      //   client: order.client,
      //   products: order.orderItems.map(orderItem => orderItem),
      //   subsidiaryExternalCode: order.orderDetails?.subsidiaryExternalCode,
      //   paymentPlanExternalCode: order.orderDetails?.paymentPlanExternalCode,
      //   priceTableCode
      // } as AvailablePromotionsParamsProps);

      const orderValidated = yield select(validateBudgetCopy(order, [{}]));

      let pix: PixPaymentProps | null = null;

      if (order?.orderDetails?.paymentOptionType === PAYMENT_OPTION_TYPE.PIX) {
        const { data } = yield call(orderService.generatePix, id);
        pix = data;
      }

      const orderItemsTotal = order.orderItems?.reduce(
        (acumulator: number, item: any) =>
          currency(acumulator, { precision: configuration?.decimals ?? 2 })
            .add(item?.soldPrice * item?.soldQuantity)
            .add(item?.tributacao?.vlTributos ?? 0)
            .add(item?.tributacao?.vlReduction ?? 0),
        0
      );

      let parsedOrder = {
        ...orderValidated,
        orderDetails: {
          ...orderValidated.orderDetails,
          clientAddress
        },
        orderTotal: currency(orderItemsTotal, { precision: configuration?.decimals ?? 2 })
          .add(order?.orderDetails?.shippingFee)
          .add(order?.orderDetails?.otherExpenses),
        taxTotal: order.orderItems?.reduce(
          (acumulator: number, item: any) => acumulator + (item?.tributacao?.vlTributos ?? 0),
          0
        ),
        reductionTotal: order.orderItems?.reduce(
          (acumulator: number, item: any) => acumulator + (item?.tributacao?.vlReduction ?? 0),
          0
        ),
        paymentPlanGroup1Total: order?.orderDetails?.usePaymentPlansGroup
          ? order?.orderItems.reduce((total: number, item: any) => {
              return item.paymentPlansGroup === '1' ? (total += item.soldPrice * item.soldQuantity) : total;
            }, 0)
          : 0,
        paymentPlanGroup2Total: order?.orderDetails?.usePaymentPlansGroup
          ? order?.orderItems.reduce((total: number, item: any) => {
              return item.paymentPlansGroup === '2' ? (total += item.soldPrice * item.soldQuantity) : total;
            }, 0)
          : 0,
        orderItems: order?.orderItems.map((item: any) => ({
          ...item,
          soldPriceWithTax: toDecimals(
            item.soldPrice +
              (item.tributacao?.values?.vlTributosUnitary ?? 0) +
              (item.tributacao?.values?.vlReductionUnitary ?? 0),
            configuration?.decimals ?? 2
          ),
          itemTotal: toDecimals(item.soldPrice * item.soldQuantity, configuration?.decimals ?? 2),
          itemTotalWithTax: toDecimals(
            item.soldPrice * item.soldQuantity + item.tributacao?.vlTributos + item.tributacao?.vlReduction,
            configuration?.decimals ?? 2
          )
        })),
        blockingSubsidiaryReason,
        pix
      };

      yield put(
        orderActions.getByIdSuccess({
          data: {
            ...parsedOrder
          }
        })
      );

      yield put(orderActions.setLoadingData(false));

      let orderVinculated = null;

      if (order?.internalCode) {
        const { data: vinculatedOrder } = yield call(orderService.getVinculatedOrder, order?.internalCode);

        if (vinculatedOrder) {
          const { data: childOrder } = yield call(orderService.getOrder, vinculatedOrder?._id);
          orderVinculated = childOrder;
        }
      }

      yield put(
        orderActions.getByIdSuccess({
          data: {
            ...parsedOrder,
            vinculatedOrder: orderVinculated
          }
        })
      );
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(orderActions.setLoadingVinculatedOrder(false));
  }
}

export function* shareOrderPdf({ order }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));
  try {
    const { data: client }: { data: ClientInterface } = yield call(clientService.getClient, order?.client?._id);

    const clientInfo = {
      email: client?.email,
      externalCode: order?.externalCode,
      governmentalCode: client?.governmentalCode,
      name: client?.name,
      paymentPlanExternalCode: order?.orderDetails?.paymentPlanExternalCode,
      paymentPlanGroup1ExternalCode: order?.orderDetails?.paymentPlanGroup1ExternalCode,
      paymentPlanGroup2ExternalCode: order?.orderDetails?.paymentPlanGroup2ExternalCode,
      phoneNumber: client?.commercialInfo?.phoneNumber || '{}',
      usePaymentPlansGroup: order?.orderDetails?.usePaymentPlansGroup
    };

    const { data: pdf } = yield call(orderService.orderPdf, order?._id, false, clientInfo);

    const reader = new FileReader();
    reader.readAsDataURL(pdf);
    reader.onloadend = async function () {
      saveAs(
        new Blob([pdf], { type: 'application/octet-stream' }),
        `${order?.orderNumber ?? '00000'} - ${order?.client?.clientName ?? 'Sem Nome'}.pdf`
      );
    };
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* downloadExcel({ order }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    const { data: file } = yield call(orderService.downloadExcel, order?._id);

    saveAs(new Blob([file], { type: 'application/octet-stream' }), `pedido-${order?.orderNumber ?? '00000'}.xlsx`);
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* getAllSelects({ client }: any) {
  yield delay(200);
  try {
    yield put(orderActions.setLoadingGetSelects(true));

    const { externalCode, userSubsidiaries } = yield select(state => state.auth.user);

    if (cancel !== undefined) cancel();

    const [{ data: clients }, { data: filiais }] = yield all([
      call(
        orderService.getClients,
        {
          userExternalCode: externalCode,
          queryString: client,
          engine: 'externalCode,name'
        },
        {
          cancelToken: new CancelToken(function executor(c) {
            cancel = c;
          })
        }
      ),
      call(domainService.getSubsidiaries, { userSubsidiaryFilter: userSubsidiaries ?? [] })
    ]);

    if (clients) {
      yield put(
        orderActions.getAllFiltersSelectSuccess({
          clients,
          filiais: filiais.sort((a: any, b: any) =>
            a.externalCode.localeCompare(b.externalCode, undefined, { numeric: true })
          )
        })
      );
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
    yield put(orderActions.setLoadingGetSelects(false));
  } finally {
    yield put(orderActions.setLoadingGetSelects(false));
  }
}

export function* deleteOrCancelOrder({ id, isDetail, isBudget }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      isBudget ? formatMessage('orderFormLabelExcludeBudgetTitle') : formatMessage('commonLabelExclude'),
      isBudget ? formatMessage('orderFormLabelExcludeBudgetMessage') : formatMessage('commonLabelExcludeItem')
    );

    if (isConfirmed) {
      yield put(orderActions.setLoadingSend(true));

      if (isBudget) {
        const { data: order } = yield call(orderService.getOrder, id);

        yield call(orderService.cancelBudget, order);
      } else {
        yield call(orderService.deleteOrder, id);
      }

      const { user } = yield select((state: AppStateType) => state.auth);

      let sellers;

      if (
        user?.userRole === USER_ROLE.DIRECTOR ||
        user?.userRole === USER_ROLE.MANAGER ||
        user?.userRole === USER_ROLE.SUPERVISOR
      ) {
        const { data: _sellers } = yield call(orderService.getSellers);

        const sellersLevel = [...userService.buildSellersLevel([..._sellers], user)];

        const initialSellers = userService.getInitialSellers(sellersLevel, user);

        sellers = [...initialSellers];
      } else {
        sellers = [user?.externalCode];
      }

      if (isDetail) {
        yield call(history.push, `/order`, {
          date: new Date().getTime()
        });
      } else {
        yield put(
          orderActions.getAllRequest({
            dateRange: [new Date(), new Date()],
            sellersSelecteds: sellers
          })
        );
      }

      yield put(orderActions.setLoadingSend(false));
      yield call(
        notification.success,
        formatMessage(isBudget ? 'orderFormLabelExcludeSuccess' : 'orderFormExcludeSuccess')
      );
    }
  } catch (error) {
    yield put(orderActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* resendOrder({ id }: any) {
  yield put(orderActions.setLoadingSend(true));
  const { user } = yield select((state: AppStateType) => state.auth);

  try {
    yield call(orderService.resendOrder, id);

    let sellers;

    if (
      user?.userRole === USER_ROLE.DIRECTOR ||
      user?.userRole === USER_ROLE.MANAGER ||
      user?.userRole === USER_ROLE.SUPERVISOR
    ) {
      const { data: _sellers } = yield call(orderService.getSellers);

      const sellersLevel = [...userService.buildSellersLevel([..._sellers], user)];

      const initialSellers = userService.getInitialSellers(sellersLevel, user);

      sellers = [...initialSellers];
    } else {
      sellers = [user?.externalCode];
    }

    yield put(
      orderActions.getAllRequest({
        dateRange: [new Date(), new Date()],
        sellersSelecteds: sellers
      })
    );

    yield put(orderActions.setLoadingSend(false));

    yield call(notification.success, formatMessage('orderFormResendSuccess'));
  } catch (error) {
    yield put(orderActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* cancelOrderTotvs({ type, payload: { id, reasonCancellation } }: orderActions.CancelOrderTotvsTypes) {
  const configuration: ConfigurationInterface = yield select(selectorConfiguration);
  const {
    form: { data: ogOrder }
  } = yield select((state: AppStateType) => state.order);

  yield put(orderActions.setLoadingData(true));
  const loading: any = notification.loading('Cancelando pedido...');

  try {
    const { data: order } = yield call(orderService.cancelOrderTotvs, { id, reasonCancellation });

    if (order) {
      const orderValidated = yield select(validateBudgetCopy(order, [{}]));

      let parsedOrder = {
        ...ogOrder,
        ...orderValidated,
        orderTotal: order.orderItems?.reduce(
          (acumulator: number, item: any) =>
            currency(acumulator)
              .add(item?.soldPrice * item?.soldQuantity)
              .add(item?.tributacao?.values?.vlST)
              .add(order?.orderDetails?.shippingFee),
          0
        ),
        taxTotal: order.orderItems?.reduce(
          (acumulator: number, item: any) =>
            currency(acumulator).add((item?.tributacao?.values?.vlST ?? 0) * item?.soldQuantity),
          0
        ),
        paymentPlanGroup1Total: order?.orderDetails?.usePaymentPlansGroup
          ? order?.orderItems.reduce((total: number, item: any) => {
              return item.paymentPlansGroup === '1' ? (total += item.soldPrice * item.soldQuantity) : total;
            }, 0)
          : 0,
        paymentPlanGroup2Total: order?.orderDetails?.usePaymentPlansGroup
          ? order?.orderItems.reduce((total: number, item: any) => {
              return item.paymentPlansGroup === '2' ? (total += item.soldPrice * item.soldQuantity) : total;
            }, 0)
          : 0,
        orderItems: order?.orderItems.map((item: any) => ({
          ...item,
          soldPriceWithTax: toDecimals(item.soldPrice + item.tributacao?.values?.vlST, configuration?.decimals ?? 2),
          itemTotal: toDecimals(item.soldPrice * item.soldQuantity, configuration?.decimals ?? 2),
          itemTotalWithTax: toDecimals(
            item.soldPrice * item.soldQuantity + item.tributacao?.values?.vlST,
            configuration?.decimals ?? 2
          )
        }))
      };

      yield put(
        orderActions.getByIdSuccess({
          data: {
            ...parsedOrder
          }
        })
      );

      yield put(orderActions.setLoadingData(false));
    }

    loading.close();
    yield call(notification.success, 'Pedido Cancelado com Sucesso');
  } catch (e) {
    console.error(e);
    yield call(notification.error, 'Houve um Erro ao cancelar pedido');
  } finally {
    loading.close();
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(GET_ALL_FILTER_SELECTS_REQUEST, getAllSelects),
  takeLatest(SHARE_PDF, shareOrderPdf),
  takeLatest(DOWNLOAD_EXCEL, downloadExcel),
  takeLatest(DELETE_OR_CANCEL_ITEM, deleteOrCancelOrder),
  takeLatest(RESEND_ITEM, resendOrder),
  takeLatest(CANCEL_ORDER_TOTVS, cancelOrderTotvs)
]);
